import { useState, useCallback, useEffect, RefObject } from 'react';
import { ElementMetadata } from '../types';
import { getElementMetadata } from '../utils/elementMetadata';

interface UseElementTargetingOptions {
  dialogRef: RefObject<HTMLDivElement | null>;
  onElementSelected: (metadata: ElementMetadata) => void;
}

interface UseElementTargetingReturn {
  isTargeting: boolean;
  cursorPos: { x: number; y: number };
  startTargeting: () => void;
  cancelTargeting: () => void;
}

export function useElementTargeting({
  dialogRef,
  onElementSelected,
}: UseElementTargetingOptions): UseElementTargetingReturn {
  const [isTargeting, setIsTargeting] = useState(false);
  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });

  const startTargeting = useCallback(() => {
    setIsTargeting(true);
  }, []);

  const cancelTargeting = useCallback(() => {
    setIsTargeting(false);
  }, []);

  const selectElement = useCallback(
    (element: HTMLElement) => {
      const metadata = getElementMetadata(element);
      onElementSelected(metadata);
      setIsTargeting(false);
    },
    [onElementSelected],
  );

  useEffect(() => {
    if (!isTargeting) return;

    let rafId: number | null = null;
    let currentTarget: HTMLElement | null = null;
    let lockTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const updateTarget = (x: number, y: number) => {
      const elements = document.elementsFromPoint(x, y);
      const target = elements.find(
        (el) =>
          !el.classList.contains('targeting-ui') &&
          el !== dialogRef.current &&
          !dialogRef.current?.contains(el) &&
          !(el instanceof SVGElement),
      ) as HTMLElement | undefined;

      if (target !== currentTarget) {
        currentTarget?.classList.remove('targeting-highlight');
        if (target) {
          target.classList.add('targeting-highlight');
        }
        currentTarget = target || null;
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      setCursorPos({ x: e.clientX, y: e.clientY });

      if (rafId !== null) return;

      rafId = requestAnimationFrame(() => {
        updateTarget(e.clientX, e.clientY);
        rafId = null;
      });
    };

    const handleClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (currentTarget) {
        currentTarget.classList.add('targeting-locked');
        const targetRef = currentTarget;
        lockTimeoutId = setTimeout(() => {
          targetRef.classList.remove('targeting-locked', 'targeting-highlight');
          lockTimeoutId = null;
        }, 400);

        selectElement(currentTarget);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        currentTarget?.classList.remove('targeting-highlight');
        cancelTargeting();
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('click', handleClick, true);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      if (lockTimeoutId !== null) {
        clearTimeout(lockTimeoutId);
      }
      currentTarget?.classList.remove('targeting-highlight', 'targeting-locked');
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('click', handleClick, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isTargeting, cancelTargeting, selectElement, dialogRef]);

  return {
    isTargeting,
    cursorPos,
    startTargeting,
    cancelTargeting,
  };
}
